/** Profile (SCR-15), Notifications (SCR-19), Projects (SCR-12) and the invitation-activation page. */
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { PageHeader, Card, EmptyState, Spinner, ErrorState, Badge, Field, Drawer } from '../../components/common/index.jsx';
import { DataTable } from '../../components/common/DataTable.jsx';
import { ProjectStatusBadge, ProjectDetail, Progress, PROJECT_STATUS } from '../projects/projectShared.jsx';
import { ButtonSpinner } from '../../components/common/Loading.jsx';
import { useOutletContext } from 'react-router-dom';
import { pushToast } from '../ui/uiSlice.js';
import { relativeTime, date } from '../../utils/formatters.js';
import * as api from '../../api/partnerPortal.api.js';
import { signOut } from '../auth/authSlice.js';
import { ROLE_NAME, UserStatusBadge, errorText } from './partnerShared.jsx';

export function PartnerProfilePage() {
  const me = useOutletContext();
  return (
    <>
      <PageHeader title="Profile" subtitle="Your account and partner organisation" />
      <div className="row g-3">
        <div className="col-lg-6"><Card title="My account" bodyClass="d-flex flex-column gap-2 t-sm">
          <div><div className="field-x-label">Name</div>{me.user.full_name}</div>
          <div><div className="field-x-label">Email</div>{me.user.email}</div>
          <div><div className="field-x-label">Mobile</div>{me.user.mobile ?? '—'}</div>
          <div><div className="field-x-label">Role</div>{ROLE_NAME[me.role]}</div>
          {me.user.designation && <div><div className="field-x-label">Designation</div>{me.user.designation}</div>}
          <div className="d-flex gap-2 mt-2"><Link to="/profile/password" className="btn btn-edit btn-sm-x"><i className="bi bi-key me-1" aria-hidden="true" />Change password</Link><Link to="/profile/sessions" className="btn btn-edit btn-sm-x"><i className="bi bi-laptop me-1" aria-hidden="true" />Active sessions</Link></div>
        </Card></div>
        <div className="col-lg-6"><Card title="Partner organisation" bodyClass="d-flex flex-column gap-2 t-sm">
          <div><div className="field-x-label">Name</div>{me.partner.name}</div>
          <div><div className="field-x-label">Partner ID</div>{me.partner.code}</div>
          <div><div className="field-x-label">Type</div>{me.partner.kind === 'company' ? 'Company partner' : 'Individual partner'}</div>
          <div><div className="field-x-label">Status</div><UserStatusBadge status={me.partner.status} /></div>
          <div className="t-xs text-muted">To change organisation details, contact your CRM administrator.</div>
        </Card></div>
      </div>
    </>
  );
}

export function PartnerNotificationsPage() {
  const qc = useQueryClient();
  const q = useInfiniteQuery({ queryKey: ['partner', 'notifications', 'all'], queryFn: ({ pageParam }) => api.notifications({ cursor: pageParam, limit: 30 }), initialPageParam: undefined, getNextPageParam: (l) => l.page?.next_cursor ?? undefined });
  const refresh = () => qc.invalidateQueries({ queryKey: ['partner', 'notifications'] });
  const all = useMutation({ mutationFn: api.markAllRead, onSuccess: refresh });
  const one = useMutation({ mutationFn: api.markRead, onSuccess: refresh });
  const navigate = useNavigate();
  if (q.isLoading) return <Spinner />;
  if (q.isError) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const rows = q.data.pages.flatMap((p) => p.data);
  const unread = q.data.pages[0]?.unread ?? 0;
  return (
    <>
      <PageHeader title="Notifications" subtitle={unread ? `${unread} unread` : 'You are all caught up'}>
        {unread > 0 && <button type="button" className="btn btn-edit btn-sm-x" onClick={() => all.mutate()}>Mark all read</button>}
      </PageHeader>
      <Card bare>
        {rows.length === 0 ? <EmptyState icon="bi-bell-slash" title="No notifications" message="Lead approvals, comments and requests show up here." /> : rows.map((n) => (
          <button key={n.id} type="button" className="d-flex w-100 text-start border-0 bg-transparent px-3 py-3 border-bottom align-items-start gap-3"
            onClick={() => { if (!n.is_read) one.mutate(n.id); if (n.partner_lead_id) navigate(`/partner/leads/${n.partner_lead_id}`); }}>
            <span className="dot-ind mt-2" style={{ background: n.is_read ? 'var(--line-2)' : 'var(--brand-500)' }} />
            <span className="flex-grow-1"><span className={`d-block t-sm ${n.is_read ? '' : 'fw-650'}`}>{n.title}</span>{n.body && <span className="d-block t-xs text-muted">{n.body}</span>}</span>
            <span className="t-2xs text-muted text-nowrap">{relativeTime(n.created_at)}</span>
          </button>
        ))}
      </Card>
      {q.hasNextPage && <div className="text-center mt-3"><button type="button" className="btn btn-edit btn-sm-x" onClick={() => q.fetchNextPage()}>Load more</button></div>}
    </>
  );
}

export function PartnerProjectsPage() {
  const [status, setStatus] = useState('');
  const [open, setOpen] = useState(null);
  const q = useInfiniteQuery({
    queryKey: ['partner', 'projects', status], queryFn: ({ pageParam }) => api.listProjects({ status, cursor: pageParam, limit: 25 }),
    initialPageParam: undefined, getNextPageParam: (l) => l.page?.next_cursor ?? undefined,
  });
  const detail = useQuery({ queryKey: ['partner', 'project', open], queryFn: () => api.getProject(open), enabled: Boolean(open) });
  const rows = (q.data?.pages ?? []).flatMap((p) => p.data);
  const columns = [
    { key: 'project_no', header: 'Project ID', render: (r) => <span className="fw-600">{r.project_no}</span> },
    { key: 'name', header: 'Project', render: (r) => (<div><div className="fw-600">{r.name}</div><div className="t-xs text-muted">{r.customer?.name ?? '—'}</div></div>) },
    { key: 'deal', header: 'Related deal', render: (r) => r.deal?.name ?? '—' },
    { key: 'pm', header: 'Project manager', render: (r) => r.project_manager?.name ?? '—' },
    { key: 'status', header: 'Status', render: (r) => <ProjectStatusBadge status={r.status} /> },
    { key: 'dates', header: 'Timeline', render: (r) => <span className="t-xs">{date(r.start_date)} → {date(r.expected_end_date)}</span> },
    { key: 'progress', header: 'Progress', render: (r) => <Progress pct={r.progress_pct} done={r.milestones_done} total={r.milestones_total} /> },
  ];
  return (
    <>
      <PageHeader title="Projects" subtitle="Projects on the deals that came from your leads — read-only" />
      <Card bare>
        <div className="d-flex gap-2 p-3 pb-2">
          <select className="form-select form-select-sm w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {Object.entries(PROJECT_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <DataTable columns={columns} rows={rows} isLoading={q.isLoading} error={q.error} onRetry={q.refetch} onRowClick={(r) => setOpen(r.id)}
          emptyTitle="No projects yet" emptyMessage="When a deal from one of your leads becomes a project, it appears here with its milestones." />
        {q.hasNextPage && <div className="p-3 text-center"><button type="button" className="btn btn-edit btn-sm-x" onClick={() => q.fetchNextPage()}>Load more</button></div>}
      </Card>
      <Drawer open={Boolean(open)} onClose={() => setOpen(null)} title={detail.data?.name ?? 'Project'} subtitle={detail.data?.project_no}>
        {detail.isLoading ? <Spinner /> : detail.data && <ProjectDetail project={detail.data} />}
      </Drawer>
    </>
  );
}

/** Public: set a password from an invitation link (/partner/activate?token=…). */
export function PartnerActivatePage() {
  const [sp] = useSearchParams();
  const token = sp.get('token') ?? '';
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [err, setErr] = useState('');
  const act = useMutation({
    mutationFn: () => api.activate({ token, password: pw }),
    onSuccess: async () => { await dispatch(signOut()); dispatch(pushToast({ tone: 'success', message: 'Account activated — you can sign in now' })); navigate('/login', { replace: true }); },
    onError: (e) => setErr(errorText(e)),
  });
  const submit = (e) => {
    e.preventDefault();
    if (!token) { setErr('This link is missing its token. Open the link from your invitation email.'); return; }
    if (pw.length < 12) { setErr('Use at least 12 characters, with upper- and lower-case letters, a number and a symbol.'); return; }
    if (pw !== pw2) { setErr('The two passwords do not match.'); return; }
    setErr(''); act.mutate();
  };
  return (
    <div className="auth-wrap" style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--surface-2)' }}>
      <form onSubmit={submit} noValidate className="card-x p-4" style={{ width: 420, maxWidth: '92vw' }}>
        <div className="text-center mb-3"><img src="/logo-full.png" alt="EnGage" style={{ height: 40 }} /></div>
        <h4 className="mb-1">Activate your partner account</h4>
        <p className="t-sm text-muted">Choose a password to finish setting up your account.</p>
        {err && <div className="alert alert-danger py-2 t-sm">{err}</div>}
        <Field label="New password" required><input type="password" className="form-control" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" autoFocus /></Field>
        <div className="mt-3"><Field label="Confirm password" required><input type="password" className="form-control" value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" /></Field></div>
        <button type="submit" className="btn btn-save w-100 mt-4" disabled={act.isPending}>{act.isPending ? <><ButtonSpinner />Activating…</> : 'Activate account'}</button>
        <div className="text-center mt-3 t-sm"><Link to="/login">Back to sign in</Link></div>
      </form>
    </div>
  );
}
